import { useAppSelector } from '@Hooks/ReduxHooks'
import { Color } from '@eduact/student-theme'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { WalletIcon } from '../../Assets/Icons'
import { useGetUserProfileQuery } from '@Features/Dashboard/DashboardSlice/Dashboard.slice'
import { SkeletonBox } from '@eduact/ed-components'
import { useTranslation } from 'react-i18next'
import NumberFormatter from '@Utilities/NumberFormatter'

type Props = {
	onClick: () => void
}

/**
 * Header trigger for the wallet. Replaces the floating bubble that used to hang
 * off the bottom-right of the header; the panel it opens is unchanged.
 */
const WalletChip: React.FC<Props> = ({ onClick }) => {
	const { isAuth } = useAppSelector(state => state.auth)
	const { data, isLoading } = useGetUserProfileQuery(undefined, { skip: !isAuth })
	const [t, i18n] = useTranslation('generic')

	const amount = data?.data.student.wallet.amount

	// Same thresholds the bubble used: empty or negative reads as a problem,
	// under 200 as a warning.
	const tone = useMemo<Color>(() => {
		if (amount === undefined) return 'darkSilver'
		if (amount <= 0) return 'red'
		if (amount < 200) return 'princetonOrange'
		return 'primary'
	}, [amount])

	if (!isAuth) return null
	if (isLoading) return <SkeletonBox isLoading width="6.5rem" height="1.75rem" />

	// MainHeaderContainer pins the whole header to `direction: ltr`, so the chip
	// was reading icon → amount → currency left to right even in Arabic. Setting
	// dir per language puts the icon on the leading edge either way: the order is
	// always icon → amount → currency in the language's own reading direction.
	// Amount and currency are separate nodes so bidi cannot reorder them.
	return (
		<ChipButton type="button" onClick={onClick} dir={i18n.language === 'ar-EG' ? 'rtl' : 'ltr'}>
			<IconSlot>
				<WalletIcon color={tone} />
			</IconSlot>
			<Amount $alert={tone === 'red'}>{amount !== undefined && NumberFormatter(amount)}</Amount>
			<Currency>{t('egp')}</Currency>
		</ChipButton>
	)
}

export default WalletChip

/**
 * Sky blue on the old platinum fill sat at ~1.6:1 contrast, which is what made
 * the chip hard to read. The fill is now a near-white with a light outline and
 * the figure is dark ink; the balance tone stays on the icon, where a colour
 * carries meaning without having to be legible as text.
 */
const ChipButton = styled.button`
	align-items: center;
	background: ${props => props.theme.colors.cultured};
	border: 1px solid ${props => props.theme.colors.platinum};
	border-radius: ${props => props.theme.borderRadii.medium}px;
	color: ${props => props.theme.colors.independence};
	cursor: pointer;
	display: flex;
	gap: 0.375rem;
	font-family: inherit;
	font-size: 0.75rem;
	font-weight: 700;
	margin: 0 1rem;
	padding: 0.25rem 0.5rem;
	transition: background 150ms ease-in-out, border-color 150ms ease-in-out;
	white-space: nowrap;
	width: fit-content;
	:hover {
		background: ${props => props.theme.colors.darkCultured};
		border-color: ${props => props.theme.colors.silver};
	}
`

// An empty or negative balance is the one state worth shouting about, and red
// is the only tone that stays readable at this size on a light chip.
const Amount = styled.span<{ $alert: boolean }>`
	color: ${props => (props.$alert ? props.theme.colors.red : props.theme.colors.independence)};
	font-weight: 700;
`

const Currency = styled.span`
	color: ${props => props.theme.colors.darkSilver};
	font-weight: 600;
`

// The source svg is 29x25; scale it down to sit on a chip line.
const IconSlot = styled.div`
	display: flex;
	svg {
		height: auto;
		width: 1.15rem;
	}
`
